import { create } from 'zustand';
import type { UserSummary } from '@/features/auth/types/auth.types';

const AUTH_STORAGE_KEY = 'auth_state';

interface AuthState {
  accessToken: string | null;
  user: UserSummary | null;
  setAuth: (accessToken: string, user: UserSummary) => void;
  setAccessToken: (accessToken: string) => void;
  clearAuth: () => void;
}

function loadPersistedAuth(): { accessToken: string | null; user: UserSummary | null } {
  try {
    const stored = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!stored) return { accessToken: null, user: null };
    return JSON.parse(stored) as { accessToken: string | null; user: UserSummary | null };
  } catch {
    return { accessToken: null, user: null };
  }
}

function persistAuth(accessToken: string | null, user: UserSummary | null) {
  if (accessToken && user) {
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ accessToken, user }));
  } else {
    localStorage.removeItem(AUTH_STORAGE_KEY);
  }
}

const persisted = loadPersistedAuth();

export const useAuthStore = create<AuthState>((set) => ({
  accessToken: persisted.accessToken,
  user: persisted.user,
  setAuth: (accessToken, user) => {
    persistAuth(accessToken, user);
    set({ accessToken, user });
  },
  setAccessToken: (accessToken) => {
    set((state) => {
      if (state.user) persistAuth(accessToken, state.user);
      return { accessToken };
    });
  },
  clearAuth: () => {
    persistAuth(null, null);
    set({ accessToken: null, user: null });
  },
}));
